<template>
  <div class="solution-list">
    <article class="solution__day">
      <ExpItem
        v-if="isSolutionLoading"
        labelText="<b style='color: #999FAB;'>Updating your Itinerary</b>"
        :descriptionText="`We’re adding your selection`"
        :isRemoveAllowed="false"
        :isLoading="false"
      >
        <div class="loading-img"></div>
      </ExpItem>
      <div v-else>
        <SavedItem
          v-for="(item, index) in reversedPois"
          :key="item.id"
          :first="index === 0"
          :activity="item"
          @click="onShowDetails(item.id)"
          @remove-item="onItemRemove(item.id)"
          :isLoading="isSolutionLoading"
        />
      </div>
    </article>
    <slot name="actions" v-bind="{nothingYet}" />
  </div>
</template>

<script>
import Vue from 'vue';
import VueCompositionAPI, {computed} from '@vue/composition-api';
Vue.use(VueCompositionAPI);
import SavedItem, {SavedPoiState} from './saved-item.vue';
import ExpItem from './exp-item.vue';
import {useSavedPlaces} from '../saved-places/savedPlaces';
import {getSolutionLoading, removeManualActivity, removePOIFromSolution} from '@/store/state/helpers';
import {useParams} from '@/router/router';
import {ItineraryListModule} from '@/store/modules/itinerary-list';
import { useStay } from '../../solution-views/stay';

export default {
  name: 'SavedItemsList',
  components: {
    SavedItem,
    ExpItem,
  },
  props: {},
  setup(_, {emit}) {
    const {savedPois, savedPoiIds, manualActivities} = useSavedPlaces();

    const destinationName = computed(() => {
      const params = useParams();
      return params.value.contextName?.replace('_', ', ');
    });

    const justAddedIds = computed(() => new Set(manualActivities.value.map((x) => x.id)));
    const didntFitActivitiesIds = computed(() => {
      const ids = savedPoiIds.value
        // remove just saved, not build yet
        .filter((id) => !justAddedIds.value.has(id))
        // remove activities without day
        .filter((id) => !ItineraryListModule.getDayById(id, destinationName.value));

      return new Set(ids);
    });

    const isJustAdded = (id) => {
      return justAddedIds.value.has(id);
    };

    const isNotIncluded = (id) => {
      return didntFitActivitiesIds.value.has(id);
    };

    const isIncluded = (id) => {
      return !isNotIncluded(id) && !isJustAdded(id);
    }

    const reversedPois = computed(() => {
      return (savedPois.value || []).map((x) => {
        const isNewItem = isJustAdded(x.id);
        const isNotIncludedItem = isNotIncluded(x.id);
        const state = isNewItem
          ? SavedPoiState.NEW
          : isNotIncludedItem
          ? SavedPoiState.NOT_INCLUDED
          : SavedPoiState.NONE;

        return {
          ...x,
          state,
        };
      })
      .sort((a, b) => a.state === SavedPoiState.NEW ? -1 : 0);
    });
    const onShowDetails = (id) => {
      emit('show-details', id);
    };

    const onItemRemove = (id) => {
      removePOIFromSolution(id, true);
    };

    const isSolutionLoading = computed(() => {
      return getSolutionLoading();
    });

    const nothingYet = computed(() => {
      return !savedPoiIds.value.some((x) => ItineraryListModule.getDayById(x, destinationName.value));
    });

    return {
      reversedPois,
      onShowDetails,
      onItemRemove,
      isSolutionLoading,
      nothingYet,
    };
  },
};
</script>

<style lang="scss">
@import '../../../tooltip.scss';
.solution-list-hover-tooltip {
  --tooltip-bg: #fff;
  --tooltip-padding: 0px;
  --tooltip-z-index: 10;
  z-index: 1000;
}
</style>

<style scoped lang="scss">
@import '../../solution-views/vue-transitions.scss';

.solution-list {
  // max-height: 50vh;
  overflow: auto;
  border-top: 1px solid #e5e5e5;
  margin-left: 26px;
  padding-bottom: 70px;
  margin-bottom: 70px;
}

.day__date:first-child:after {
  content: '';
  height: 1px;
  position: relative;
  width: 100%;
  display: block;
  z-index: 0;
}

.solution__day {
  position: relative;
  // margin-bottom: 15px;
}

.day__date {
  padding-left: 0;
  border-top: 1px solid var(--toneBw-5);
}
.solution__day.itinerary-header {
  margin-bottom: 0;
}
.itinerary-header ~ .solution__day .day__date {
  border: 0px;
}
.day__date:before {
  border-color: #e5e5e5;
}
.day__activities {
  margin-bottom: 10px;
}

.stay-header__caption {
  display: inline-block;
  font-style: normal;
  font-weight: 700;
  font-size: 11px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
  margin-right: 0.5em;
}

.day__label {
  margin: 0px;
  padding: 8px 0 0 0;
  font-size: 11px;
  margin-bottom: 5px;

  font-style: normal;
  font-weight: 400;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.5);

  & > strong {
    color: #071d3b;
  }
}

.day__hint {
  color: var(--toneDark-40);
  font-size: var(--tSize-xs);
  font-style: normal;
  font-weight: 300;
  line-height: 130%;
  margin: 0;
}

.day__hint strong {
  font-weight: 700;
}

.icon {
  background: linear-gradient(0deg, #e7e7e7, #e7e7e7);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  color: #c7c7c7;
}

.gear-icon {
  position: absolute;
  top: 50%;
  transform: translate(-5px, -38%);
  padding: 0;
  border: 0;
  background: 0;
  cursor: pointer;
  z-index: 10;
}

.radio-section {
  background: #071d3b;
  border-radius: 100px;
  padding: 10px;
}

.radio-section > * {
  margin: 10px;
}

.popover-content {
  width: 340px;
  height: 333px;
}

.recommended-item {
  background: #ffffff;
}

.recommended-item {
  border-top-left-radius: 10px;
}

.recommended-item:last-child {
  border-bottom-left-radius: 10px;
}

.stay-details {
  margin-bottom: 15px;
}

.stay-header {
  padding: 10px 0;
  font-size: var(--tSize-xs);
  // border-top: 1px solid var(--toneBw-5);
}

.stay-header__duration {
  display: inline-block;
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.5);
}

.price {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 118.14%;
  text-align: right;
  letter-spacing: -0.005em;
  color: #071d3b;
  right: 25px;
  display: flex;
  flex-direction: column;
}

.price:before {
  content: 'Per night';
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 118.14%;
  text-align: right;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.37);
  white-space: nowrap;
}

.overviewImage {
  background-position: center;
  background-repeat: no-repeat;
  width: 40px;
  height: 40px;
  overflow: hidden;
  position: relative;
}

.overviewImage img {
  width: 30px;
  height: 30px;
  object-fit: cover;
  border-radius: 5px;
  position: absolute;
  top: 0;
  left: 0;
}

.overviewImage img + img {
  top: auto;
  bottom: 0;
  right: 0;
  left: auto;
}

// .fix-btn {
//   padding: 0 10px;
//   border-radius: 5px;
//   height: 30px;
//   display: flex;
//   align-items: center;
//   margin-left: auto;
// }

.unschedulled-activity:nth-child(2) {
  opacity: 1;
}
.unschedulled-activity:nth-child(3) {
  opacity: 0.5;
}
.unschedulled-activity:nth-child(4) {
  opacity: 0.2;
}

.loading-img {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: #e4e5e8;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml,%3Csvg width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 0C10.5661 0 10.2143 0.351767 10.2143 0.785714V5.5C10.2143 5.93393 10.5661 6.28571 11 6.28571C11.434 6.28571 11.7857 5.93393 11.7857 5.5V0.785714C11.7857 0.351767 11.434 0 11 0ZM3.78125 2.99554C3.58016 2.99554 3.37813 3.07128 3.2247 3.2247C2.91785 3.53153 2.91785 4.02277 3.2247 4.32961L6.55579 7.66888C6.86261 7.97573 7.36206 7.97573 7.66889 7.66888C7.97579 7.36206 7.97571 6.86264 7.66889 6.55579L4.3296 3.2247C4.1762 3.07128 3.98234 2.99554 3.78125 2.99554ZM18.2188 2.99554C18.0177 2.99553 17.8238 3.07127 17.6704 3.2247L14.3311 6.55579C14.0243 6.86264 14.0243 7.36206 14.3311 7.66888C14.6379 7.97573 15.1373 7.97573 15.4442 7.66888L18.7753 4.32961C19.0822 4.02278 19.0821 3.53154 18.7753 3.2247C18.6219 3.07129 18.4198 2.99554 18.2188 2.99554ZM0.785714 10.2143C0.351764 10.2143 0 10.5661 0 11C0 11.4339 0.351764 11.7857 0.785714 11.7857H5.5C5.93392 11.7857 6.28571 11.4339 6.28571 11C6.28571 10.5661 5.93392 10.2143 5.5 10.2143H0.785714ZM16.5 10.2143C16.0661 10.2143 15.7143 10.5661 15.7143 11C15.7143 11.434 16.0661 11.7857 16.5 11.7857H21.2143C21.6482 11.7857 22 11.434 22 11C22 10.5661 21.6482 10.2143 21.2143 10.2143H16.5ZM7.11234 14.1019C6.91127 14.1019 6.70924 14.1777 6.55579 14.3311L3.2247 17.6704C2.91783 17.9772 2.91785 18.4685 3.2247 18.7753C3.53152 19.0821 4.02275 19.0821 4.3296 18.7753L7.66889 15.4442C7.97571 15.1374 7.97571 14.6379 7.66889 14.3311C7.51549 14.1777 7.31343 14.1019 7.11234 14.1019V14.1019ZM14.8877 14.1019C14.6866 14.1019 14.4845 14.1777 14.3311 14.3311C14.0242 14.6379 14.0243 15.1374 14.3311 15.4442L17.6704 18.7753C17.9772 19.0821 18.4685 19.0821 18.7753 18.7753C19.0821 18.4685 19.0821 17.9772 18.7753 17.6704L15.4442 14.3311C15.2908 14.1777 15.0887 14.1019 14.8877 14.1019ZM11 15.7143C10.5661 15.7143 10.2143 16.0661 10.2143 16.5V21.2143C10.2143 21.6482 10.5661 22 11 22C11.4339 22 11.7857 21.6482 11.7857 21.2143V16.5C11.7857 16.0661 11.4339 15.7143 11 15.7143Z' fill='%23A2A2A2'/%3E%3C/svg%3E%0A");
  border-radius: 5px;
  z-index: 1;
}
</style>
